/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, Autocomplete, TextField, Chip,
    Alert, Button,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { usePopupFailure } from '../../contexts/PopupFailureContext';
import Iconify from '../Iconify';
import DentallyAvailabilityStep from './DentallyAvailabilityStep';
import { parseDentallyOffsetPage } from '../../utils/dentallyPagination';
import useDentallyReasonOptions from '../../hooks/useDentallyReasonOptions';
import { callApi } from '../../utils/apiCall';
import { useSelector } from '../../redux/store';
import {
    arePracticeUserSelectionsEqual,
    filterPracticeUserSelections,
    selectPracticeUsersForPractice,
} from '../../redux/slices/practiceUser';
import { grey } from '../../theme/palette';
import { bookingStepLabelStyle, bookingStepPanelStyle } from '../../styles/DentallyBookingStyle';

/**
 * Practice-configured appointment types and availability for Dentally booking.
 *
 * Props:
 *   practiceId              — required
 *   selectedSlot            — currently selected slot (controlled, from parent)
 *   onSlotSelected          — called with slot object or null
 *   selectedDentallyTreatment — selected CRM appointment type with practiceId and reason (controlled)
 *   onTreatmentSelected     — (appointmentType, crmName, suggestedPayment, reason) => void
 *   assignees               — controlled array of booked-with practice user objects
 *   onAssigneesChanged      — called when booked-with changes
 */
export default function DentallyTreatmentAndAvailability({
    practiceId,
    selectedSlot,
    onSlotSelected,
    selectedDentallyTreatment,
    onTreatmentSelected,
    assignees,
    onAssigneesChanged,
    availSearchState,
    onAvailSearchStateChange,
    disableGutters,
}) {
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const practiceUsers = useSelector((state) => selectPracticeUsersForPractice(state, practiceId));

    const { reasons, loading: reasonsLoading, error: reasonsError } = useDentallyReasonOptions(practiceId, { verifiedOnly: true });
    const [crmTreatments, setCrmTreatments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { report: reportFailure } = usePopupFailure();
    useEffect(() => { if (error) reportFailure(error, 'Availability'); }, [error]);
    useEffect(() => { if (reasonsError) reportFailure(reasonsError, 'Availability'); }, [reasonsError]);
    const [assignToState, setAssignToState] = useState(assignees || []);
    const [loadedPracticeId, setLoadedPracticeId] = useState(null);

    // Sync only assignees that belong to the fully loaded current practice.
    // A globally mounted create-lead dialog can otherwise retain practice-A IDs
    // after navigation and submit them under practice B.
    useEffect(() => {
        const scopedAssignees = filterPracticeUserSelections(practiceUsers, assignees || []);
        setAssignToState((current) => (
            arePracticeUserSelectionsEqual(current, scopedAssignees) ? current : scopedAssignees
        ));
        if (!arePracticeUserSelectionsEqual(assignees || [], scopedAssignees)) {
            onAssigneesChanged?.(scopedAssignees);
        }
    }, [assignees, onAssigneesChanged, practiceUsers]);

    useEffect(() => {
        if (!practiceId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);

        setCrmTreatments([]);
        setLoadedPracticeId(null);
        // Follow CRM pagination using a local offset, never a server-supplied URL.
        const loadChoices = async () => {
            const items = [];
            for (let offset = 0; offset < 10000; offset += 500) {
                // Pagination is sequential so incomplete results never become bookable choices.
                // eslint-disable-next-line no-await-in-loop
                const response = await callApi(
                    `/api/v1/practices/${practiceId}/treatments/?enabled=true&ordering=-is_starred,treatment_item__name&limit=500&offset=${offset}`
                );
                if (cancelled) return;
                if (!response.ok || !Array.isArray(response.data?.results)) {
                    throw new Error('Could not load appointment types');
                }
                const pagination = parseDentallyOffsetPage(response.data, { limit: 500, offset });
                const page = response.data.results;
                items.push(...page);
                if (!pagination.hasMore) {
                    setCrmTreatments(items
                        .filter((t) => t.enabled !== false && t.dentally_appointment_reason?.trim())
                        .map((t) => ({
                            ...t,
                            practiceId,
                            practiceTreatmentId: t.id,
                            name: t.treatment_item?.name || '',
                            dentally_appointment_reason: t.dentally_appointment_reason?.trim() || '',
                        })));
                    setLoadedPracticeId(practiceId);
                    return;
                }
                if (page.length !== 500) throw new Error('Incomplete appointment types');
            }
            throw new Error('Too many appointment types');
        };
        loadChoices().catch(() => {
            if (!cancelled) setError('Could not load appointment types. Please try again.');
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });

        // eslint-disable-next-line consistent-return
        return () => { cancelled = true; };
    }, [practiceId]);

    const scopedTreatments = String(loadedPracticeId) === String(practiceId) ? crmTreatments : [];
    const starredTreatments = useMemo(
        () => scopedTreatments.filter((t) => t.is_starred).slice(0, 5),
        [scopedTreatments]
    );

    const handleChipClick = useCallback((choice) => {
        if (String(choice.practiceId) !== String(practiceId) || !reasons.includes(choice.dentally_appointment_reason)) return;
        // Keep metadata on the object: React state setters only retain argument one.
        // A clinical catalogue fee must not overwrite the chosen booking payment.
        onTreatmentSelected(choice, choice.name, 0, choice.dentally_appointment_reason);
        onSlotSelected(null);
    }, [onTreatmentSelected, onSlotSelected, practiceId, reasons]);

    const handleClearTreatment = useCallback(() => {
        onTreatmentSelected(null, null, 0, null);
        onSlotSelected(null);
    }, [onTreatmentSelected, onSlotSelected]);

    const handleSetAssign = useCallback((values) => {
        const scopedValues = filterPracticeUserSelections(practiceUsers, values);
        setAssignToState(scopedValues);
        onAssigneesChanged?.(scopedValues);
    }, [onAssigneesChanged, practiceUsers]);

    // Choosing a slot used to fuzzy-match the Dentally practitioner's name
    // against practice users and silently make that person the lead's ASSIGNEE.
    // Removed, for three reasons:
    //
    //  1. The clinician and the lead's owner are different roles. The
    //     practitioner is already captured on the slot; assigning the lead to
    //     them is what put Dentally-synced clinicians into "Assigned to", which
    //     is not the list the rest of the CRM offers.
    //  2. It decided by name similarity — one shared word was enough — so it
    //     could assign the wrong person outright.
    //  3. Since "Assigned to" was narrowed to the roles the CRM actually
    //     assigns, a practitioner picked here would be dropped again at submit
    //     by filterPracticeUserSelections, saving a booking with NO assignee
    //     and no indication that anything had been discarded.
    //
    // Assignment is now only ever what the operator picked in "Assigned to".

    const displayName = selectedDentallyTreatment?.name || '';
    const hasSelectedType = selectedDentallyTreatment
        && String(selectedDentallyTreatment.practiceId) === String(practiceId);

    // disableGutters — drops the dialog inset so the sections span the same width
    // as the CRM-only step's sections. Inside a Dialog (CreateLeadDialogWrapper)
    // the inset is still wanted, so it defaults to off.
    return (
        <Box sx={{ px: disableGutters ? 0 : 3, py: disableGutters ? 0 : 2 }}>
            {/* ── Treatment ──────────────────────────────────────────────── */}
            <Box sx={{ mb: 2.5 }}>
            <Typography sx={bookingStepLabelStyle}>Appointment type</Typography>
            <Box sx={bookingStepPanelStyle}>

            {(error || reasonsError) && <Alert severity='error' sx={{ mb: 1.5 }}>{error || reasonsError}</Alert>}
            {!loading && !error && scopedTreatments.length === 0 && <Alert severity='info' sx={{ mb: 1.5 }}>Configure appointment types in Settings → Integrations → Dentally to book here.</Alert>}
            {hasSelectedType && !selectedDentallyTreatment.dentally_appointment_reason && (
                <Alert severity='warning' sx={{ mb: 1.5 }}>Map an appointment reason for this choice in Settings → Integrations → Dentally before booking.</Alert>
            )}

            {!hasSelectedType ? (
                <Box>
                    {/* Starred CRM treatments as quick-select chips */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                        {starredTreatments.map((t) => {
                            let textColor = grey[400];
                            if (isLight) textColor = grey[800];
                            return (
                                <Button
                                    key={t.id}
                                    variant='outlined'
                                    onClick={() => handleChipClick(t)}
                                    disabled={!reasons.includes(t.dentally_appointment_reason)}
                                    startIcon={
                                        <Iconify
                                            icon='material-symbols:circle'
                                            width={20}
                                            height={20}
                                            sx={{ color: grey[400] }}
                                        />
                                    }
                                    sx={{
                                        borderRadius: '50px',
                                        border: '1px solid rgba(145, 158, 171, 0.32)',
                                        m: '0 !important',
                                        p: '4px 12px 4px 5px',
                                        height: '32px',
                                        color: textColor,
                                        bgcolor: 'transparent',
                                        fontWeight: 400,
                                        textTransform: 'none',
                                        fontSize: '0.875rem',
                                        minWidth: 'auto',
                                        '&:hover': {
                                            bgcolor: 'rgba(145, 158, 171, 0.08)',
                                            borderColor: 'rgba(145, 158, 171, 0.48)',
                                        },
                                    }}
                                >
                                    {t.name}
                                    {t.dentally_treatment_id && t.dentally_treatment_id !== '' && (
                                        <Iconify icon='mdi:link-variant' width={14} sx={{ ml: 0.5, color: '#00AB55' }} />
                                    )}
                                    {t.dentally_appointment_reason && t.dentally_appointment_reason.trim() && (
                                        <Iconify icon='mdi:calendar-clock-outline' width={14} sx={{ ml: 0.5, color: '#855CF8' }} />
                                    )}
                                </Button>
                            );
                        })}
                        {starredTreatments.length === 0 && !loading && (
                            <Typography variant='caption' sx={{ color: 'text.disabled', ml: 0.5 }}>
                                Pin your favourite appointment types in Settings to see them here
                            </Typography>
                        )}
                    </Box>

                    {/* Booking choices configured in Integrations, including reason-only mappings. */}
                    <Autocomplete
                        options={scopedTreatments}
                        getOptionDisabled={(t) => !reasons.includes(t.dentally_appointment_reason)}
                        getOptionLabel={(t) => t.name || ''}
                        onChange={(_, val) => val ? handleChipClick(val) : handleClearTreatment()}
                        loading={loading || reasonsLoading}
                        isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                        renderOption={(props, t) => {
                            const reason = t.dentally_appointment_reason;
                            return (
                                <Box component='li' {...props} key={t.id}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography variant='body2'>{t.name}</Typography>
                                            {!reasons.includes(reason) && <Typography variant='caption' color='text.secondary'>Appointment reason needs configuring in Integrations</Typography>}
                                        </Box>
                                        {reason && <Chip size='small' label={reason} color='info' variant='outlined' sx={{ fontSize: 10, height: 20, maxWidth: 100 }} />}
                                    </Box>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size='small'
                                label='Appointment type'
                                placeholder='Search configured appointment types…'
                            />
                        )}
                        size='small'
                        sx={{ mb: 1 }}
                    />
                </Box>
            ) : (
                /* Selected treatment info bar — no background/border/padding of its
                   own (2026-08-17, Raj: "two boards around the treatment itself
                   which is unnecessary"). It already sits inside the section panel,
                   so its own card chrome drew a second rounded border inside the
                   first. Now it is layout only. */
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {/* No "Treatment" caption here (2026-08-17): the numbered
                        "1 TREATMENT" heading sits directly above this bar, so the
                        caption said the same word twice in ~40px of vertical space. */}
                    <Box sx={{ flex: '1 1 auto', minWidth: '120px' }}>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: 'text.primary' }}>
                            {displayName}
                        </Typography>
                    </Box>
                    {selectedDentallyTreatment.dentally_appointment_reason ? (
                        <Chip
                            size='small'
                            color='success'
                            icon={<Iconify icon='mdi:link-variant' width={14} />}
                            label={selectedDentallyTreatment.dentally_appointment_reason}
                        />
                    ) : (
                        <Chip
                            size='small'
                            color='warning'
                            icon={<Iconify icon='mdi:alert-outline' width={14} />}
                            label='Appointment reason not configured'
                        />
                    )}
                    <Button
                        variant='text'
                        size='small'
                        onClick={handleClearTreatment}
                        startIcon={<Iconify icon='mdi:pencil-outline' width={16} />}
                        sx={{
                            textTransform: 'none', fontWeight: 500, fontSize: '0.8125rem',
                            color: '#00A76F', px: 1.5, py: 0.75, borderRadius: '8px',
                            '&:hover': { bgcolor: 'rgba(0, 167, 111, 0.08)' },
                        }}
                    >
                        Change
                    </Button>
                </Box>
            )}

            </Box>
            </Box>

            {/* ── Availability ─────────────────────────────────────────────
                No wrapping card + "Availability" label here any more (2026-08-17):
                DentallyAvailabilityStep already renders its own numbered "2 TEAM"
                and "3 AVAILABILITY" headers, each on its own grey panel. The outer
                card added a third nesting level and repeated the word Availability
                directly above step 3's identical heading. Dropping it makes steps
                1 / 2 / 3 read as siblings, which is what the numbering implies. */}
            <DentallyAvailabilityStep
                practiceId={practiceId}
                selectedSlot={selectedSlot}
                onSelect={onSlotSelected}
                assignToState={assignToState}
                onAssignToChange={handleSetAssign}
                availSearchState={availSearchState}
                onAvailSearchStateChange={onAvailSearchStateChange}
            />
        </Box>
    );
}

DentallyTreatmentAndAvailability.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selectedSlot: PropTypes.object,
    onSlotSelected: PropTypes.func.isRequired,
    selectedDentallyTreatment: PropTypes.object,
    onTreatmentSelected: PropTypes.func.isRequired,
    assignees: PropTypes.array,
    onAssigneesChanged: PropTypes.func,
    availSearchState: PropTypes.object,
    onAvailSearchStateChange: PropTypes.func,
    disableGutters: PropTypes.bool,
};

DentallyTreatmentAndAvailability.defaultProps = {
    selectedSlot: null,
    selectedDentallyTreatment: null,
    assignees: [],
    onAssigneesChanged: null,
    availSearchState: null,
    onAvailSearchStateChange: null,
    disableGutters: false,
};
